import { CircleAlert } from 'lucide-react';

import { getApprovalCopy } from '@apps/web/components/phoebe_chat/approval_copy';
import { type V3ChatActions } from '@apps/web/features/phoebe_chat_v3/model/actions';
import { type V3ToolEntry } from '@apps/web/features/phoebe_chat_v3/model/transcript';
import { V3AmbientButton } from '@apps/web/features/phoebe_chat_v3/ui/ambient_components';

export function V3GenericApprovalCard({
    entry,
    callId,
    actions,
}: {
    readonly entry: V3ToolEntry;
    readonly callId: string;
    readonly actions: V3ChatActions;
}) {
    const isDeciding = actions.decidingCallIds[callId] === true;
    const canDecide = actions.onToolDecision !== undefined;
    const copy = getApprovalCopy(entry.name, entry.arguments);
    return (
        <section
            aria-label={`Approval required: ${copy.title}`}
            data-testid="v3-generic-approval-card"
            className="w-full max-w-[700px] min-w-0 overflow-hidden rounded-[14px] border border-[#DBE3E3] bg-white text-[#002429] antialiased shadow-[0_1px_2px_rgba(0,36,41,0.04),0_4px_14px_rgba(0,36,41,0.05)]"
        >
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 border-b border-[#FFDE9E] bg-[#FFF2DB] px-4 py-2.5 text-[#8C3B00]">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                    <CircleAlert
                        aria-hidden="true"
                        className="size-3.5 shrink-0"
                    />
                    <span className="text-[11px] leading-[14px] font-semibold tracking-[0.04em] uppercase">
                        Review before continuing
                    </span>
                </div>
                <span className="text-xs leading-4">
                    Nothing has changed yet
                </span>
            </div>
            <div className="px-4 py-3.5">
                <h3 className="text-base leading-5 font-semibold break-words">
                    {copy.title}
                </h3>
                <p className="pt-0.5 text-xs leading-4 text-[#617578]">
                    {copy.summary}
                </p>
                {copy.detailList !== undefined ? (
                    <div className="mt-3 rounded-lg border border-[#DBE3E3] bg-[#F7FAFA] px-3 py-2.5">
                        <p className="text-xs font-medium text-[#42595E]">
                            {copy.detailList.label}
                        </p>
                        <ul className="mt-1.5 space-y-1 text-xs leading-4 text-[#617578]">
                            {copy.detailList.items.map((item, index) => (
                                <li key={`${item}-${index}`}>{item}</li>
                            ))}
                        </ul>
                    </div>
                ) : null}
                {copy.messagePreview !== null ? (
                    <p className="mt-3 rounded-lg border border-[#DBE3E3] bg-[#F7FAFA] p-3 text-sm leading-5 break-words whitespace-pre-wrap text-[#002429]">
                        {copy.messagePreview}
                    </p>
                ) : null}
            </div>
            {canDecide ? (
                <div className="flex flex-wrap justify-end gap-2 border-t border-[#E9F0F0] px-4 py-3">
                    <V3AmbientButton
                        type="button"
                        variant="outline"
                        disabled={isDeciding}
                        onClick={() => {
                            void actions.onToolDecision?.(callId, 'deny');
                        }}
                    >
                        Deny
                    </V3AmbientButton>
                    <V3AmbientButton
                        type="button"
                        variant="primary"
                        loading={isDeciding}
                        onClick={() => {
                            void actions.onToolDecision?.(callId, 'allow_once');
                        }}
                    >
                        Approve
                    </V3AmbientButton>
                </div>
            ) : null}
        </section>
    );
}
